import { type QueryClient, useMutation, useQuery } from '@tanstack/react-query'
import { type PromoteUserData } from 'api-client/users/model'

import { notify } from '~/features/notifications'

import { apiClient } from '~/shared/api'
import { useDecodedToken } from '~/shared/model/auth/useAuth'

import { Box } from '~/components/Box'
import { Button } from '~/components/Button'
import { Stack } from '~/components/Stack'
import { Text } from '~/components/Text'

import { organizationsOwnedQuery, organizationsUsersQuery } from '../queries'

import { DeleteUser } from './DeleteUser'

export const ActiveUsersList = ({ queryClient }: { queryClient: QueryClient }) => {
  const decodedToken = useDecodedToken()
  const isOrgAccount = Boolean(decodedToken?.organization_id)
  const { data: activeUsers, refetch } = useQuery(organizationsUsersQuery(isOrgAccount))

  const { data: userOrgs } = useQuery(organizationsOwnedQuery())
  const isOrgOwner = userOrgs?.some(user => user.id === String(decodedToken?.organization_id))

  const promoteUserMutation = useMutation({
    mutationFn: (data: PromoteUserData) => apiClient.organizations.promoteUser(data),
    onSuccess: () => notify.success('User promoted'),
    onError: () => notify.error('Failed to promote user'),
  })

  const handlePromoteUser = async (user: { id: string; readOnly: boolean }) => {
    promoteUserMutation.mutate({
      user_id: String(user.id),
      read_only: user.readOnly,
      organization_id: String(decodedToken?.organization_id),
    })
    await queryClient.invalidateQueries(organizationsUsersQuery(isOrgAccount))
    await refetch()
  }

  return (
    <Stack space="xsmall">
      <Text size="medium" color="gray600">
        Active members
      </Text>
      {activeUsers?.length ? (
        activeUsers
          .sort((a: any, b: any) => {
            if (a.owner && !b.owner) return -1
            if (!a.owner && b.owner) return 1
            return 0
          })
          .map((user: any) => (
            <Stack direction="horizontal" justify="between" align="center" key={user.email}>
              <Stack direction={'horizontal'} justify={'start'} align={'start'}>
                <Box>
                  <Text lineHeight="medium">{user.givenName + ' ' + user.familyName}</Text>
                </Box>

                {/* org owner tag */}
                {user?.owner && (
                  <Box
                    backgroundColor={'green500'}
                    padding={'xxsmall'}
                    paddingX={'small'}
                    borderRadius={'medium'}
                  >
                    <Text size={'small'} color="white">
                      Owner
                    </Text>
                  </Box>
                )}
                {/* read_only user tag */}
                {user?.readOnly && (
                  <Box
                    backgroundColor={'blue500'}
                    padding={'xxsmall'}
                    paddingX={'small'}
                    borderRadius={'medium'}
                    maxWidth={'large'}
                  >
                    <Text size={'small'} color="white">
                      Read-Only
                    </Text>
                  </Box>
                )}
                {!user.readOnly && !user.owner && isOrgOwner && (
                  <Box>
                    <Button
                      onPress={() => {
                        void handlePromoteUser({ id: String(user.id), readOnly: true })
                      }}
                      size="small"
                      color="neutral"
                    >
                      Change role to Read-Only
                    </Button>
                  </Box>
                )}
                {user.readOnly && !user.owner && isOrgOwner && (
                  <Box>
                    <Button
                      onPress={() => {
                        void handlePromoteUser({ id: String(user.id), readOnly: false })
                      }}
                      size="small"
                      color="neutral"
                    >
                      Change role to Read-Write
                    </Button>
                  </Box>
                )}
              </Stack>

              <Stack direction="horizontal" justify="end" align="center">
                {user.email}

                {isOrgOwner && (
                  <Box>
                    <DeleteUser userId={user.id} />
                  </Box>
                )}
              </Stack>
            </Stack>
          ))
      ) : (
        <Text>There are no users yet.</Text>
      )}
    </Stack>
  )
}
